// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAccountShipTosDataView } from "@insite/client-framework/Store/Data/AccountShipTos/AccountShipTosSelectors";
import loadAccountShipToCollection from "@insite/client-framework/Store/Data/AccountShipTos/Handlers/LoadAccountShipToCollection";
import saveShipToCollection from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SaveShipToCollection";
import updateSearchFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { AccountShipToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isModalOpen: boolean;
    closeModalHandler: () => void;
    extendedStyles?: AssignShipToAddressModalStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    accountShipTosDataView: getAccountShipTosDataView(state, state.pages.userSetup.getAccountShipToCollectionParameter),
    parameter: state.pages.userSetup.getAccountShipToCollectionParameter,
});

const mapDispatchToProps = {
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface AssignShipToAddressModalStyles {
    modal?: ModalPresentationProps;
    shipToContainer?: GridContainerProps;
    shipToNumberGridItem?: GridItemProps;
    shipToNumberLabel?: TypographyPresentationProps;
    shipToNumberText?: TypographyPresentationProps;
    addressInfoGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    assignShippingAddressGridItem?: GridItemProps;
    assignShippingAddressCheckbox?: CheckboxPresentationProps;
    pagination?: PaginationPresentationProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    stickyCell?: InjectableCss;
}

export const assignShipToAddressModalStyles: AssignShipToAddressModalStyles = {
    modal: { sizeVariant: "large" },
    shipToContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    shipToNumberGridItem: {
        width: [4, 2, 2, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    shipToNumberLabel: {
        css: css`
            margin-bottom: 5px;
            font-size: 15px;
            font-weight: 500;
        `,
    },
    addressInfoGridItem: { width: [8, 6, 6, 6, 7] },
    assignShippingAddressGridItem: { width: [12, 4, 4, 4, 3] },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    stickyCell: {
        css: css``,
    },
};

const AssignShipToAddressModal = ({
    isModalOpen,
    accountShipTosDataView,
    parameter,
    closeModalHandler,
    extendedStyles,
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
    toaster,
}: Props) => {
    useEffect(() => {
        if (!accountShipTosDataView.value && !accountShipTosDataView.isLoading) {
            loadAccountShipToCollection(parameter);
        }
    });

    useEffect(() => {
        setShipToCollection(accountShipTosDataView.value);
    }, [accountShipTosDataView.value]);

    const [shipToCollection, setShipToCollection] = React.useState<AccountShipToModel[] | undefined>();
    const [updatedIndices, setUpdatedIndices] = React.useState<{ value: boolean; index: number }[]>([]);

    const [styles] = useState(() => mergeToNew(assignShipToAddressModalStyles, extendedStyles));

    const assignShippingAddressChangeHandler = (value: boolean, index: number) => {
        if (shipToCollection) {
            const currentShipToCollection = cloneDeep(shipToCollection);
            currentShipToCollection[index].assign = value;
            if (!updatedIndices.find(t => t.index === index)) {
                updatedIndices.push({ value, index });
                setUpdatedIndices(updatedIndices);
            }
            setShipToCollection(currentShipToCollection);
        }
    };

    const handleChangePage = (page: number) => {
        updateSearchFields({ page });
    };

    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    const saveButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (!shipToCollection) {
            return;
        }

        saveShipToCollection({
            shipToCollection,
            onSuccess: () => {
                setUpdatedIndices([]);
                closeModalHandler();
                toaster.addToast({ body: translate("User Created/Updated."), messageType: "success" });
            },
            onError: (errorMessage: string) => {
                setUpdatedIndices([]);
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const cancelClickHandler = () => {
        if (!shipToCollection) {
            closeModalHandler();
            return;
        }
        const currentShipToCollection = cloneDeep(shipToCollection);
        updatedIndices.forEach(({ index, value }) => {
            currentShipToCollection[index].assign = !value;
        });
        setShipToCollection(currentShipToCollection);
        setUpdatedIndices([]);
        closeModalHandler();
    };

    return (
        <Modal
            headline={translate("Assign Ship To Address")}
            isOpen={isModalOpen}
            handleClose={closeModalHandler}
            data-test-selector="assignShiptoAddressModal"
        >
            <StyledWrapper
                css={css`
                    overflow: auto;
                    width: 100%;
                `}
            >
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader tight title="Ship To #" {...styles.stickyCell}>
                            {translate("Ship To #")}
                        </DataTableHeader>
                        <DataTableHeader tight title="Customer">
                            {translate("Customer Name")}
                        </DataTableHeader>
                        <DataTableHeader tight title="Address">
                            {translate("Address")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {shipToCollection?.map((shipTo, index) => (
                            <DataTableRow key={`${shipTo.shipToNumber}-${index}`}>
                                <DataTableCell {...styles.stickyCell}>
                                    <Typography as="p" {...styles.shipToNumberText} aria-labelledby="shipToNumber">
                                        {shipTo.shipToNumber}
                                    </Typography>
                                </DataTableCell>
                                <DataTableCell>
                                    <AddressInfoDisplay
                                        address1={shipTo.address}
                                        city={shipTo.city}
                                        state={shipTo.state}
                                        postalCode=""
                                        extendedStyles={styles.addressInfoDisplay}
                                    />
                                </DataTableCell>
                                <DataTableCell>
                                    <Checkbox
                                        {...styles.assignShippingAddressCheckbox}
                                        checked={shipTo.assign}
                                        onChange={(e, value) => assignShippingAddressChangeHandler(value, index)}
                                        disabled={shipTo.shipToNumber.length === 0}
                                        data-test-selector="assignShipToAddressModal_assign"
                                        data-test-key={shipTo.shipToNumber}
                                    >
                                        {translate("Assign Shipping Address")}
                                    </Checkbox>
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {accountShipTosDataView.pagination && accountShipTosDataView.pagination.totalItemCount > 1 && (
                <StyledWrapper
                    css={css`
                        margin-top: 20px;
                    `}
                >
                    <Pagination
                        {...styles.pagination}
                        resultsCount={accountShipTosDataView.pagination.totalItemCount}
                        currentPage={accountShipTosDataView.pagination.page}
                        resultsPerPage={accountShipTosDataView.pagination.pageSize}
                        resultsPerPageOptions={accountShipTosDataView.pagination.pageSizeOptions}
                        onChangePage={handleChangePage}
                        onChangeResultsPerPage={handleChangeResultsPerPage}
                        prevPage={accountShipTosDataView.pagination.prevPageUri}
                        nextPage={accountShipTosDataView.pagination.nextPageUri}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper className="modal-actions">
                <Button
                    {...styles.saveButton}
                    onClick={saveButtonClickHandler}
                    data-test-selector="assignShipToAddressModal_saveButton"
                >
                    {translate("Save")}
                </Button>
                <Button {...styles.cancelButton} onClick={cancelClickHandler}>
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(AssignShipToAddressModal));
